<script lang="ts">
    import cep from 'cep-promise'
    import { onMount, createEventDispatcher } from 'svelte'
    const dispatch = createEventDispatcher()
    import type { PaymentLink } from '../../../modules/paymentsApi/payment-links/payment-link.entity'
    import { CreateInvoiceDto, GatewayCustomerCard } from '../../../modules/paymentsApi/purchases/dto/create-invoice.dto'
    import type { GatewayPublicInfo } from '../../../modules/paymentsApi/gateways/gateway.entity'
    import { PurchaseType } from '../../../modules/paymentsApi/purchases/purchase.enum'
    import type { Purchase } from '../../../modules/paymentsApi/purchases/purchase.entity'
    import type { Charge } from '../../../modules/paymentsApi/charges/charge.entity'
    import { CreateGatewayCustomer } from '../../../modules/paymentsApi/purchases/dto/create-customer.dto'
    import { CreateGatewayCard } from '../../../modules/paymentsApi/customer-cards/dto/create-customer-card.dto'
    import { PaymentMethods, PaymentMethodsFriendly } from '../../../modules/paymentsApi/charges/charge.enum'
    import type { GetBoletoDto } from '../../../modules/paymentsApi/charges/get-boleto.dto'
    import PaymentsPixShow from '../../payments/payments-pix-show.svelte'

    export let paymentLinkId: string = null // id do link de pagamento
    export let referenceId: string = null // refere-se a um id único do cliente/customer, que pode possuir várias cobranças, portanto pode-se repetir
    export let customer: CreateGatewayCustomer = null
    export let gatewayId: string = null

    // O QUE está sendo cobrado. Vem do link de pagamento ou, na cobrança avulsa, da tela de emissão
    // — que é do operador.
    //
    // Antes esses campos eram digitados aqui, numa etapa 1 deste componente. Só que este componente
    // é a tela do PAGADOR: pedir a ele o valor e a descrição da própria compra invertia os papéis, e
    // pela url pública (/public/checkout?g=) qualquer um podia abrir o checkout e escolher quanto
    // pagar. Quem define o que se cobra é quem emite.
    export let purchaseDescription: string = null
    export let purchaseTotalValue: number = null
    export let purchaseType: PurchaseType = null
    export let purchaseMethod: string = null
    // Teto de parcelas definido na emissão, para a cobrança avulsa. Com link, o teto vem dele.
    // Nulo = sem limite próprio, e vale só o que a bandeira permite.
    export let maxInstallmentsAllowed: number = null
    // Entrada do carnê definida na emissão. Com link, vem dele. Nulo = carnê sem entrada.
    export let entryValueAllowed: number = null

    let invoice: CreateInvoiceDto = new CreateInvoiceDto()
    invoice.card = new CreateGatewayCard()
    invoice.customer = new CreateGatewayCustomer()
    if (window.location.href.includes('127.0.0.1') || window.location.href.includes('localhost')) {
        invoice.customer.name = 'Cleyton R Santos'
        invoice.customer.addressCEP = '38412066'
        invoice.customer.addressCity = 'Uberlândia'
        invoice.customer.addressNeighborhood = 'Tubalina'
        invoice.customer.addressNumber = '2377'
        invoice.customer.addressState = 'MG'
        invoice.customer.addressStreet = 'Avenida Getúlio Vargas'
        invoice.customer.document = '06025849650'
        invoice.customer.email = 'crsantos555@gmail.com'
        invoice.customer.phone = '34999706580'
        invoice.purchaseDescription = 'Teste de compra (' + Math.round(new Date().getTime() / 1000).toString() + ')'
        invoice.purchaseMethod = PaymentMethods.credit
        invoice.purchaseTotalValue = 2.26
        invoice.purchaseType = PurchaseType.recurring_continuous
        // Cartão de teste do Mercado Pago (Mastercard). O número anterior, 5207355064233420, é do
        // sandbox do pagar.me e estava declarado como visa — a bandeira errada e um BIN que o
        // Mercado Pago não reconhece davam 'Bin not found' na cobrança, sem pista da causa.
        // No pagar.me, use 4000000000000010 / visa.
        invoice.card.brand = 'master'
        invoice.card.expirationMonth = '11'
        invoice.card.expirationYear = '2030'
        invoice.card.name = 'Cleyton R Santos'
        invoice.card.number = '5031433215406351'
        invoice.card.securityCode = '123'
    }
    let gateway: GatewayPublicInfo = null
    let err: { title: string; message: string; code: string } = null
    let maxInstallments = 0
    let purchaseTotalLabel = 'Valor total'

    $: {
        if (invoice?.purchaseType === PurchaseType.recurring_continuous) {
            purchaseTotalLabel = 'Valor mensal'
        } else {
            purchaseTotalLabel = 'Valor total'
        }
    }

    onMount(async () => {
        await loadCheckout()
    })

    function calculateInstallmentValue(value: number, installments: number) {
        const installment = Math.round((value * 100) / installments) / 100
        return window.api.utils.utils.toReal(installment)
    }

    // Entrada do carnê, como quem paga precisa ver: anunciada à parte, e o número que aparece é o
    // de parcelas DEPOIS dela. Por dentro são N vencimentos com o primeiro ajustado — o mesmo que a
    // tela de emissão mostra, para o pagador ver exatamente o que foi ofertado.
    $: carneComEntrada =
        invoice?.purchaseType === PurchaseType.recurring_fixed &&
        invoice?.purchaseEntryValue > 0 &&
        invoice?.purchaseEntryValue < invoice?.purchaseTotalValue &&
        invoice?.purchaseTotalInstallments > 1

    $: resumoEntrada = carneComEntrada
        ? 'Entrada de ' +
          window.api.utils.utils.toReal(invoice.purchaseEntryValue) +
          ' + ' +
          (invoice.purchaseTotalInstallments - 1) +
          'x de ' +
          calculateInstallmentValue(invoice.purchaseTotalValue - invoice.purchaseEntryValue, invoice.purchaseTotalInstallments - 1)
        : ''

    // O teto de parcelas é derivado da bandeira, do meio, do tipo de compra e do teto informado por
    // quem emitiu. Enquanto era calculado só no clique da bandeira, quem chegava com ela já
    // preenchida ficava preso em "primeiro selecione a bandeira", porque nada mais disparava o cálculo.
    $: maxInstallments = calculateMaxInstallments(
        invoice?.card?.brand,
        invoice?.purchaseMethod,
        invoice?.purchaseType,
        gateway,
        paymentLink?.purchaseTotalInstallments ?? maxInstallmentsAllowed,
    )

    function calculateMaxInstallments(
        brand: string,
        purchaseMethod: string,
        purchaseType: PurchaseType,
        gateway: GatewayPublicInfo,
        // teto definido na emissão: vem do link ou, na cobrança avulsa, da tela de emissão.
        // null significa "não foi limitado", e aí só a bandeira manda.
        tetoDaEmissao: number,
    ): number {
        // todo: no futuro considerar permitir parcelamento no boleto. se sim, uma tela para o usuário escolher quantas parcelas.
        if (purchaseMethod === PaymentMethods.bankslip) {
            return 1
        }
        let brandMax = 0
        if (purchaseType === PurchaseType.recurring_fixed) {
            brandMax = 24
        } else {
            brandMax = gateway?.brands?.find((el) => el.brand === brand)?.installments || 0
        }
        if (tetoDaEmissao) {
            return tetoDaEmissao > brandMax ? brandMax : tetoDaEmissao
        }
        return brandMax
    }

    function setBrand(brand: string) {
        invoice.card.brand = brand
    }

    let step = 0
    let purchaseId: string = ''
    let chargeId: string = ''
    let paymentLink: PaymentLink = null
    // empresa dona do link, usada só para as estatísticas públicas do checkout
    let statCompanyId: string = null
    async function loadCheckout() {
        purchaseId = ''
        chargeId = ''
        err = null
        invoice.referenceId = referenceId // se o paymentlink possui um referenceId, então ele é preservado
        invoice.uniqueId = null
        invoice.purchaseTotalInstallments = 0
        if (paymentLinkId) {
            try {
                paymentLink = await window.api.payments.paymentLinks.getOne(paymentLinkId)
            } catch (error) {
                // A api recusa o link vencido com uma mensagem própria. Sem este try o erro subia
                // pelo onMount e o pagador ficava num "Carregando..." eterno, sem saber o motivo.
                step = 10
                err = {
                    title: 'Link indisponível',
                    message: error?.message || error || 'Não foi possível carregar este link de pagamento.',
                    code: 'payment-link-unavailable',
                }
                return
            }
            if (paymentLink) {
                console.log(paymentLink)
                // canal público: o pagador não tem sessão, então a empresa vem do dono do link
                statCompanyId = paymentLink.companyId
                window.api.users.statistics.tickPublicOnce(statCompanyId, 'pub.checkout.open')
                invoice.purchaseDescription = paymentLink.purchaseDescription
                invoice.purchaseQuantity = 1 // paymentLink.purchaseQuantity
                invoice.purchaseTotalValue = paymentLink.purchaseTotalValue
                invoice.purchaseType = paymentLink.purchaseType
                invoice.gatewayId = paymentLink.gatewayId
                invoice.purchaseLaunchType = paymentLink.purchaseLaunchType
                invoice.purchaseServiceId = paymentLink.purchaseServiceId
                invoice.purchasePaymentId = paymentLink.purchasePaymentId
                invoice.purchaseUserId = paymentLink.purchaseUserId
                invoice.purchaseMethod = paymentLink.paymentMethodsAllowed
                //paymentLink.paymentMethodsAllowed
                // No carnê a quantidade de vencimentos faz parte da oferta, não é escolha de quem
                // paga. E em pix/boleto não existe a tela que a escolheria — a de parcelamento é do
                // cartão. Sem copiar aqui, o carnê chegava ao backend com 0, virava 1 e a venda
                // inteira colapsava numa cobrança só.
                if (paymentLink.purchaseType === PurchaseType.recurring_fixed) {
                    invoice.purchaseTotalInstallments = paymentLink.purchaseTotalInstallments || 1
                    // a entrada faz parte da oferta, igual aos vencimentos: quem paga não a escolhe
                    invoice.purchaseEntryValue = paymentLink.purchaseEntryValue || null
                }
                step = 2
                gatewayId = paymentLink.gatewayId // mesmo que informe gatewayId, se passou um link, então o link prevalece.
            } else {
                step = 10
                err = {
                    title: 'Link inválido',
                    message: 'Link de pagamento não encontrado.',
                    code: 'payment-link-not-found',
                }
                return
            }
        } else {
            if (!gatewayId) {
                step = 10
                err = {
                    title: 'Link inválido',
                    message: 'Gateway não informado.',
                    code: 'no-gatewayid',
                }
                return
            }
            // Cobrança avulsa: a especificação chega pronta da tela de emissão. Sem ela não há o
            // que cobrar — e não é o pagador quem vai preencher.
            if (!purchaseDescription || !purchaseTotalValue || !purchaseType || !purchaseMethod) {
                step = 10
                err = {
                    title: 'Link inválido',
                    message: 'Cobrança não informada.',
                    code: 'no-purchase-spec',
                }
                return
            }
            invoice.purchaseDescription = purchaseDescription
            invoice.purchaseQuantity = 1
            invoice.purchaseTotalValue = purchaseTotalValue
            invoice.purchaseType = purchaseType
            invoice.purchaseMethod = purchaseMethod
            // mesma razão do link: no carnê os vencimentos vêm da emissão, não da tela do pagador
            if (purchaseType === PurchaseType.recurring_fixed) {
                invoice.purchaseTotalInstallments = maxInstallmentsAllowed || 1
                invoice.purchaseEntryValue = entryValueAllowed || null
            }
            step = 2
        }
        // gateway
        gateway = await window.api.payments.gateways.getOnePublic(gatewayId) // // todo: tratamento de erro no response (500 / 404...)
        if (!gateway) {
            step = 10
            err = {
                title: 'Link inválido',
                message: 'Gateway não encontrado.',
                code: 'gateway-not-found',
            }
            return
        }
        invoice.gatewayId = gatewayId
        console.log('gateway', gateway)
        // customer
        if (customer) {
            invoice.customer = customer
        }
        console.log('invoice', invoice)
        console.log('gateway', gateway)
        dispatch('load', gateway)
    }

    async function searchAddress(force: boolean) {
        if (!force && invoice.customer.addressStreet) {
            console.log('no-cep', invoice.customer.addressStreet)
            return
        }
        const cleanCEP = invoice.customer.addressCEP?.replace('-', '') || ''
        if (cleanCEP.length !== 8) {
            window.error('CEP inválido.')
            return
        }
        try {
            const res = await cep(cleanCEP)
            invoice.customer.addressStreet = res.street || ''
            invoice.customer.addressState = res.state || ''
            invoice.customer.addressCity = res.city || ''
            invoice.customer.addressNeighborhood = res.neighborhood || ''
        } catch (error) {
            invoice.customer.addressStreet = ''
            invoice.customer.addressState = ''
            invoice.customer.addressCity = ''
            invoice.customer.addressNeighborhood = ''
        }
    }

    function evaluatePurchaseShow() {
        step = 3
    }

    async function evaluateCustomer() {
        // clean infos
        if (invoice.customer.document) {
            invoice.customer.document = window.api.utils.utils.sanitizeDocument(invoice.customer.document)
        }
        if (invoice.customer.phone) {
            invoice.customer.phone = window.api.utils.utils.sanitizePhone(invoice.customer.phone)
        }
        if (invoice.customer.name?.length < 5 || !invoice.customer.name?.includes(' ')) {
            window.error('Informe o nome completo')
            return
        }
        if (gateway.requiredCustomerInfo.includes('email')) {
            if (!invoice.customer.email || !invoice.customer.email?.includes('@')) {
                window.error('Informe um e-mail')
                return
            }
        }
        if (gateway.requiredCustomerInfo.includes('phone')) {
            if (!invoice.customer.phone || invoice.customer.phone?.length < 10) {
                window.error('Informe um telefone com DDD')
                return
            }
            // adiciona nono dígito e código do país
            invoice.customer.phone = window.api.utils.utils.phoneNormalize(invoice.customer.phone, '00')
        }
        if (requires('document')) {
            if (invoice.customer.document?.length < 11) {
                window.error('Documento inválido')
                return
            }
            if (!window.api.utils.utils.validateCNPJ(invoice.customer.document) && !window.api.utils.utils.validateCPF(invoice.customer.document)) {
                window.error('Documento inválido')
                return
            }
        }
        // CEP e número só são cobrados por gateways que pedem endereço
        if (requires('address')) {
            if (invoice.customer.addressCEP) {
                invoice.customer.addressCEP = invoice.customer.addressCEP.replace('-', '')
            }
            if (invoice.customer.addressCEP?.length !== 8) {
                window.error('Informe um CEP válido')
                return
            }
            if (!invoice.customer.addressNumber) {
                window.error('Informe o número do imóvel.\nCaso não possua um número informe 0 ou sn')
                return
            }
        }

        const proxima = stepAfterCustomer()
        // nada mais a preencher: parte direto para a cobrança
        if (proxima === null) {
            await tryBill()
            return
        }
        if (proxima === 4) await searchAddress(false)
        step = proxima
    }

    async function evaluateAddress() {
        if (invoice.customer.addressCEP) {
            invoice.customer.addressCEP = invoice.customer.addressCEP.replace('-', '')
        }
        if (invoice.customer.addressCEP?.length !== 8) {
            window.error('Informe um CEP válido')
            return
        }
        if (invoice.customer.addressStreet?.trim().length === 0) {
            window.error('Informe o endereço')
            return
        }
        if (invoice.customer.addressNumber?.trim() === '') {
            window.error('Informe o número do imóvel.\nCaso não possua um número informe 0 ou sn')
            return
        }
        if (invoice.customer.addressNeighborhood?.trim().length === 0) {
            window.error('Informe o bairro')
            return
        }
        if (invoice.customer.addressCity?.trim().length === 0) {
            window.error('Informe a cidade')
            return
        }
        if (invoice.customer.addressState?.trim().length !== 2) {
            window.error('Informe o estado')
            return
        }

        if (invoice.purchaseMethod === PaymentMethods.credit) {
            step = 5
        } else {
            await tryBill()
        }
    }

    async function evaluateCard() {
        if (invoice.card.number) {
            invoice.card.number = invoice.card.number.replace(/[- ]/g, '')
            invoice.card.number = invoice.card.number.replace(/\s/g, '') // Remove espaços em branco spaces
        }
        // o número é conferido sozinho primeiro, senão uma bandeira errada culpa o número
        if (!window.api.utils.utils.validateCreditCard(invoice.card?.number?.toString())) {
            window.error('Número do cartão inválido. Confira o número informado. (1)')
            return
        }
        if (!invoice.card.brand) {
            window.error('Selecione a bandeira do cartão clicando na imagem')
            return
        }
        // barra aqui a bandeira que contradiz o número: o gateway só recusaria depois de cobrar,
        // com uma mensagem que não ajuda o portador em nada
        const bandeiraDoNumero = window.api.utils.utils.detectCardBrand(invoice.card.number?.toString())
        if (bandeiraDoNumero && bandeiraDoNumero !== invoice.card.brand) {
            window.error('A bandeira selecionada não corresponde ao número do cartão. Confira o número e a bandeira.')
            return
        }
        if (invoice.card.name?.length < 3 || !invoice.card.name?.includes(' ')) {
            window.error('Informe o nome impresso no cartão')
            return
        }
        if (!invoice.card.expirationMonth || !invoice.card.expirationYear) {
            window.error('Informe a data de vencimento do cartão')
            return
        }
        let date = new Date(parseInt(invoice.card.expirationYear), parseInt(invoice.card.expirationMonth) - 1, 31)
        if (date.getTime() - new Date().getTime() < 0) {
            window.error('O cartão informado está vencido')
            return
        }
        if (!invoice.card.securityCode || invoice.card.securityCode?.length < 3 || invoice.card.securityCode?.length > 5) {
            window.error('Informe um código de segurança válido')
            return
        }
        if (invoice.purchaseType !== PurchaseType.recurring_continuous) {
            if (!invoice.purchaseTotalInstallments) {
                window.error('Informe a quantidade de parcelas')
                return
            }
            // trocar a bandeira depois de escolher as parcelas pode derrubar o teto: sem isso o
            // número antigo seguiria para o gateway
            if (maxInstallments && invoice.purchaseTotalInstallments > maxInstallments) {
                invoice.purchaseTotalInstallments = maxInstallments
            }
        }
        invoice.card.expirationMonth = invoice.card.expirationMonth.toString()
        invoice.card.expirationYear = invoice.card.expirationYear.toString()
        await tryBill()
    }

    let statusBill: number = 0
    function showProgress(recursive = false) {
        if (!recursive) {
            statusBill = 1
        }
        if (statusBill >= 98 || statusBill === 0) {
            statusBill = 98
            return
        }
        statusBill += (100 - statusBill) * 0.005
        setTimeout(() => showProgress(true), 40)
    }

    async function tryBill() {
        showProgress()
        step = 7
        console.log('INVOICE', invoice)

        let result: { purchase: Purchase; charge: Charge; status: string; message: string; gatewayError?: boolean }
        try {
            result = await window.api.payments.purchases.createInvoice(invoice)
        } catch (error) {
            err = {
                title: 'Erro ao enviar',
                message: 'Erro desconhecido ao tentar enviar a cobrança',
                code: error.message || error || 'unknown 003',
            }
            step = 10
            window.api.users.statistics.tickPublic(statCompanyId, 'pub.checkout.error')
            console.error(error)
            return
        }

        console.log('result', result)
        statusBill = 100

        if (!result) {
            err = {
                title: 'Erro',
                message: 'Erro desconhecido ao tentar enviar a cobrança',
                code: 'unknown 001',
            }
            step = 10
            window.api.users.statistics.tickPublic(statCompanyId, 'pub.checkout.error')
            return
        }
        // A cobrança não chegou a ser criada no gateway. Cai aqui antes do tratamento de
        // 'pending' porque os dois deixam a assinatura pendente: sem esta checagem a tela dizia
        // "a instituição bancária está analisando" para uma requisição que sequer foi aceita, e o
        // cliente saía achando que tinha pago.
        if (result.gatewayError) {
            purchaseId = result.purchase?.purchaseId || ''
            err = {
                title: 'Não foi possível concluir o pagamento',
                message: result.message || 'A operadora recusou a cobrança. Confira os dados e tente novamente.',
                code: purchaseId || 'gateway-error',
            }
            step = 10
            window.api.users.statistics.tickPublic(statCompanyId, 'pub.checkout.declined')
            return
        }
        if (result.status === 'pending') {
            purchaseId = result.purchase.purchaseId
            chargeId = result.charge.chargeId
            window.api.users.statistics.tickPublic(statCompanyId, 'pub.checkout.pending')
            if (result.charge.paymentMethod === PaymentMethods.bankslip) {
                step = 11
            } else if (result.charge.paymentMethod === PaymentMethods.pix) {
                // o pix cai direto no QR Code: o cliente precisa pagar agora, não depois
                step = 12
            } else {
                step = 9
            }

            return
        }
        if (result.status === 'active') {
            purchaseId = result.purchase.purchaseId
            chargeId = result.charge.chargeId
            step = 8
            window.api.users.statistics.tickPublic(statCompanyId, 'pub.checkout.paid')
            return
        }
        if (result.status === 'canceled') {
            step = 10
            err = {
                message: result.message,
                title: 'Não autorizado',
                code: result.status,
            }
            window.api.users.statistics.tickPublic(statCompanyId, 'pub.checkout.declined')
            return
        }
        err = {
            title: 'Erro',
            message: 'Erro na cobrança: ' + result?.status + ' / ' + result?.message,
            code: result?.status || 'unknown 002',
        }
        console.log('err', err, result)
        step = 10
        window.api.users.statistics.tickPublic(statCompanyId, 'pub.checkout.error')
    }

    let boleto: GetBoletoDto = null
    async function showBoleto(chargeId: string) {
        boleto = null
        boleto = await window.api.payments.charges.getBoleto(chargeId)
        if (!boleto || !boleto.bankslipUrl) {
            alert('Ocorreu um erro ao tentar imprimir o boleto.\nCaso o erro continue entre em contato conosco.')
            return
        }
        window.open(boleto.bankslipUrl, '_blank')
    }

    // O gateway informa de quais dados do pagador precisa (requiredCustomerInfo). O checkout monta
    // o caminho a partir disso em vez de assumir que todos pedem a mesma coisa — o Mercado Pago,
    // por exemplo, precisa só de documento e e-mail, então a etapa de endereço não faz sentido.
    function requires(info: string): boolean {
        return !!gateway?.requiredCustomerInfo?.includes(info)
    }

    // Próxima etapa depois dos dados do pagador. null significa que não há mais nada a preencher
    // e a cobrança já pode ser feita.
    function stepAfterCustomer(): number {
        if (requires('address')) return 4
        if (invoice.purchaseMethod === PaymentMethods.credit) return 5
        return null
    }

    // Sequência real de telas deste gateway e meio de pagamento, na ordem. Serve ao indicador de
    // etapas: com o caminho variando por gateway, um número fixo de pontinhos passaria a prometer
    // telas que não vão aparecer — o Mercado Pago com pix, por exemplo, tem só duas.
    function flowSteps(): number[] {
        const steps = [2, 3]
        if (requires('address')) steps.push(4)
        if (invoice.purchaseMethod === PaymentMethods.credit) steps.push(5)
        return steps
    }

    // Etapa anterior à do cartão: endereço quando o gateway pede, senão os dados do pagador.
    // Usada pelos botões de voltar, para que nenhum deles leve a uma tela que este gateway
    // não usa.
    function stepBeforeCard(): number {
        return requires('address') ? 4 : 3
    }

    function backFromError() {
        // Volta para a última tela que este meio de pagamento realmente usou.
        //
        // A condição é sobre o crédito, não sobre os outros meios: só ele passa pela tela de
        // cartão (ver evaluateAddress, que manda os demais do endereço direto para a cobrança).
        // Antes a regra listava o boleto como exceção e jogava todo o resto para a tela de cartão,
        // então o pix caía numa tela que nunca tinha visto e que não se aplica a ele — e o mesmo
        // aconteceria com qualquer meio novo.
        step = invoice.purchaseMethod === PaymentMethods.credit ? 5 : stepBeforeCard()
    }
</script>

<div class="checkout">
    {#if step === 0}
        <div class="-box -loading">
            <div class="-line">Carregando...</div>
        </div>
    {/if}
    {#if step === 2}
        <div class="-box -box-purchase-show">
            <div class="spacer" />
            <div class="-line -padding">
                <label for="" class="-label">Descrição da compra</label>
                <input disabled bind:value={invoice.purchaseDescription} type="text" />
            </div>
            <div class="-line -inline -padding">
                <!-- <div class="-grow -mr-8">
                    <label for="" class="-label">Quantidade</label>
                    <input
                        bind:value={invoice.purchaseQuantity}
                        disabled
                        type="text"
                        class="-purchase-quantity -text-center"
                    />
                </div> -->
                <div class="-grow">
                    <label for="" class="-label">{purchaseTotalLabel}</label>
                    <input value={window.api.utils.utils.toReal(invoice.purchaseTotalValue)} disabled type="text" class="-text-center" />
                </div>
            </div>
            {#if resumoEntrada}
                <div class="-card-recurring">{resumoEntrada}</div>
            {/if}
            <div class="spacer" />
            <div class="-line -inline acts">
                <div class="fake-button" />
                <div class="-breadcrumb">
                    {#each flowSteps() as etapa}
                        <span>{etapa === step ? '◉' : '○'}</span>
                    {/each}
                </div>
                <button on:click={evaluatePurchaseShow}>Avançar</button>
            </div>
        </div>
    {/if}
    {#if step === 3}
        <div class="-box -box-customer">
            <div class="spacer" />
            <div class="-line -padding">
                {#if invoice.purchaseMethod === PaymentMethods.credit}
                    <label for="" class="-label">Nome completo do PROPRIETÁRIO do cartão</label>
                {:else}
                    <label for="" class="-label">Nome completo do responsável pelo pagamento</label>
                {/if}
                <input type="text" bind:value={invoice.customer.name} />
            </div>
            {#if requires('document')}
                <div class="-line -padding">
                    {#if invoice.purchaseMethod === PaymentMethods.credit}
                        <label for="" class="-label">CPF do PROPRIETÁRIO do cartão</label>
                    {:else}
                        <label for="" class="-label">CPF do responsável pelo pagamento</label>
                    {/if}
                    <input type="text" inputmode="numeric" bind:value={invoice.customer.document} />
                </div>
            {/if}
            {#if gateway.requiredCustomerInfo.includes('email')}
                <div class="-line -padding">
                    <label for="" class="-label">E-mail</label>
                    <input type="text" inputmode="email" bind:value={invoice.customer.email} />
                </div>
            {/if}
            {#if gateway.requiredCustomerInfo.includes('phone')}
                <div class="-line -padding">
                    <label for="" class="-label">Celular</label>
                    <input type="text" inputmode="tel" bind:value={invoice.customer.phone} />
                </div>
            {/if}
            {#if requires('address')}
                <div class="-line -inline -padding">
                    <div class="-mr-8">
                        <!-- "de cobrança do cartão" só faz sentido no crédito; no pix e no boleto
                             é simplesmente o endereço de quem paga -->
                        {#if invoice.purchaseMethod === PaymentMethods.credit}
                            <label for="">CEP de cobrança do cartão</label>
                        {:else}
                            <label for="">CEP</label>
                        {/if}
                        <input maxlength="10" type="text" inputmode="numeric" class="-customer-zip" bind:value={invoice.customer.addressCEP} />
                    </div>
                    <div class="-line -grow">
                        <label for="">Nº imóvel</label>
                        <input type="text" class="-grow" bind:value={invoice.customer.addressNumber} />
                    </div>
                </div>
            {/if}
            <div class="spacer" />
            <div class="-line -inline acts">
                <button on:click={() => (step = 2)}>Voltar</button>
                <div class="-breadcrumb">
                    {#each flowSteps() as etapa}
                        <span>{etapa === step ? '◉' : '○'}</span>
                    {/each}
                </div>
                <button on:click={evaluateCustomer}>Avançar</button>
            </div>
        </div>
    {/if}
    {#if step === 4}
        <div class="-box -box-address">
            <div class="spacer" />
            <div class="-line -padding">
                <label for="" class="-label">CEP</label>
                <input type="text" inputmode="numeric" class="-address-zip" bind:value={invoice.customer.addressCEP} />
            </div>
            <div class="-line -padding">
                <label for="" class="-label">Rua</label>
                <input type="text" bind:value={invoice.customer.addressStreet} />
            </div>
            <div class="-line -inline -padding">
                <div class="-mr-8">
                    <label for="">Número</label>
                    <input type="text" class="-address-number" bind:value={invoice.customer.addressNumber} />
                </div>
                <div class="-line -grow">
                    <label for="">Complemento</label>
                    <input type="text" class="-address-complement" bind:value={invoice.customer.addressComplement} />
                </div>
            </div>
            <div class="-line -padding">
                <label for="" class="-label">Bairro</label>
                <input type="text" class="-address-neighborhood" bind:value={invoice.customer.addressNeighborhood} />
            </div>
            <div class="-line -inline -padding">
                <div class="-line -grow -mr-8">
                    <label for="">Cidade</label>
                    <input type="text" class="-address-city" bind:value={invoice.customer.addressCity} />
                </div>
                <div>
                    <label for="">Estado</label>
                    <input type="text" class="-address-state" bind:value={invoice.customer.addressState} />
                </div>
            </div>
            <div class="spacer" />
            <div class="-line -inline acts">
                <button on:click={() => (step = 3)}>Voltar</button>
                <div class="-breadcrumb">
                    {#each flowSteps() as etapa}
                        <span>{etapa === step ? '◉' : '○'}</span>
                    {/each}
                </div>
                <button on:click={evaluateAddress}>Avançar</button>
            </div>
        </div>
    {/if}
    {#if step === 5}
        <div class="-box -box-card">
            <div class="spacer" />
            <div class="-line -padding">
                <label for="-label">Nome no cartão</label>
                <input type="text" class="-name" bind:value={invoice.card.name} />
            </div>

            <div class="-line -padding">
                <div class="-line">
                    <label for="" class="-label">Número do cartão</label>
                    <input placeholder="xxxx xxxx xxxx xxxx" type="text" inputmode="numeric" class="-number" bind:value={invoice.card.number} />
                </div>
            </div>
            <div class="-line -flag -padding">
                <label for="">Selecione a bandeira</label>
                <div class="-inline">
                    {#each gateway.brands as brand}
                        <button on:click={() => setBrand(brand.brand)} class="button-flag" class:button-flag-active={brand.brand === invoice.card.brand}>
                            <img src="/img/card_{brand.brand}.png" alt="" />
                        </button>
                    {/each}
                </div>
            </div>
            <div class="-line -inline -padding">
                <div class="-month-year">
                    <label for="" class="-label">Validade</label>
                    <div class="-inline">
                        <select name="" id="" class="-month" bind:value={invoice.card.expirationMonth}>
                            <option value="" />
                            {#each Array(12) as month, i}
                                <option value={(i + 1).toString().padStart(2, '0')}>{(i + 1).toString().padStart(2, '0')}</option>
                            {/each}
                        </select>
                        <select name="" id="" class="-year" bind:value={invoice.card.expirationYear}>
                            <option value="" />
                            {#each Array(12) as month, i}
                                <option value={(i + new Date().getFullYear()).toString()}>{(i + new Date().getFullYear()).toString()}</option>
                            {/each}
                        </select>
                    </div>
                </div>

                <div>
                    <label for="" class="-label">Cód. Segurança</label>
                    <input type="text" inputmode="numeric" class="-sec" bind:value={invoice.card.securityCode} />
                </div>
            </div>
            {#if invoice.purchaseType === PurchaseType.recurring_continuous}
                <div class="-line -padding">
                    <label for="" class="-label">Cobrança recorrente mensal</label>
                    <input disabled value={window.api.utils.utils.toReal(invoice.purchaseTotalValue)} type="text" class="-recurring-value" />
                    <div class="-card-recurring">
                        Esta é uma cobrança recorrente.<br />
                        Todos os meses este valor será cobrado de seu cartão.
                    </div>
                </div>
            {:else if carneComEntrada}
                <!-- com entrada os vencimentos fazem parte da oferta: deixar o pagador reduzi-los
                     mudaria o combinado, já que a entrada continuaria a mesma -->
                <div class="-line -padding">
                    <div class="label">Pagamento</div>
                    <input disabled value={resumoEntrada} type="text" class="-recurring-value" />
                    <div class="-card-recurring">
                        Esta é uma cobrança parcelada em {invoice.purchaseTotalInstallments} vencimentos.<br />
                        Cada um é cobrado do seu cartão na data devida.
                    </div>
                </div>
            {:else}
                <div class="-line -padding">
                    <div class="label">Pagar em</div>
                    <select name="" id="" class="" bind:value={invoice.purchaseTotalInstallments}>
                        {#if !maxInstallments}
                            <!-- com a bandeira já escolhida, mandar escolher a bandeira é um beco sem saída -->
                            <option selected value={0}>
                                {invoice.card.brand ? '[parcelamento indisponível para esta bandeira]' : '[primeiro selecione a bandeira do cartão]'}
                            </option>
                        {:else}
                            {#each Array(maxInstallments) as _, i}
                                <option value={i + 1}>{i + 1}x de {calculateInstallmentValue(invoice.purchaseTotalValue, i + 1)}</option>
                            {/each}
                        {/if}
                    </select>
                    <div class="-card-recurring">
                        Esta é uma cobrança recorrente.<br />
                        Apenas o valor da parcela é descontado do limite do cartão.
                    </div>
                </div>
            {/if}
            <div class="spacer" />
            <div class="-line -inline acts">
                <!-- volta para o endereço, ou direto para os dados do pagador se o gateway não pede endereço -->
                <button on:click={() => (step = stepBeforeCard())}>Voltar</button>
                <div class="-breadcrumb">
                    {#each flowSteps() as etapa}
                        <span>{etapa === step ? '◉' : '○'}</span>
                    {/each}
                </div>

                <button on:click={evaluateCard}>Concluir</button>
            </div>
        </div>
    {/if}
    {#if step === 7}
        <div class="-box -box-billing">
            <!-- <button on:click={() => (step = 5)}>voltar</button> -->
            <div class="spacer" />
            <div class="-line -padding">
                <div class="-trying">Processando...</div>
            </div>
            <div class="spacer" />
            <div class="-bill-bar-container">
                <div style="width:{statusBill}%;" class="-bill-bar" />
            </div>
        </div>
    {/if}
    {#if step === 8}
        <div class="-box -box-success">
            <div class="spacer" />
            <div class="-line -padding">
                <div class="-success">Sucesso</div>
                <div class="-subtitle">Compra concluída. ID do pagamento:</div>
                <div class="-track">{purchaseId || ''}</div>
            </div>
            <div class="spacer" />
        </div>
    {/if}
    {#if step === 9}
        <div class="-box -box-success">
            <div class="spacer" />
            <div class="-line -padding">
                <div class="-success">Em análise</div>
                <div class="-subtitle">
                    A compra está sendo analisada pela instituição bancária.<br />
                    Assim que for concluída avisaremos a você.<br />
                    Não é necessário aguardar nessa tela.<br />
                    Guarde o ID desta transação:
                </div>
                <div class="-track">{purchaseId || ''}</div>
            </div>
            <div class="spacer" />
        </div>
    {/if}
    {#if step === 10}
        <div class="-box -box-error">
            <div class="spacer" />
            <div class="-line -padding">
                <div class="-error">{err.title}</div>
                <div class="-subtitle">{err.message}</div>
                <div class="-track">Código: {err.code}</div>
            </div>
            <div class="spacer" />
            <div class="-line -inline acts">
                <button on:click={backFromError}>Voltar</button>
                <div class="spacer" />
            </div>
        </div>
    {/if}
    {#if step === 11}
        <div class="-box -box-success">
            <div class="spacer" />
            <div class="-line -padding">
                <div class="-success">Boleto criado com sucesso</div>
                <div class="-subtitle">
                    Sua compra foi concluída e está aguardando o pagamento do boleto.<br />
                    Guarde o ID desta transação:
                </div>
                <div class="-track">{purchaseId || ''}</div>
                <div class="text-center mt-3">
                    <button class="btn btn-primary" on:click={() => showBoleto(chargeId)}>Exibir boleto</button>
                </div>
            </div>
            <div class="spacer" />
        </div>
    {/if}
    {#if step === 12}
        <div class="-box -box-success">
            <div class="spacer" />
            <div class="-line -padding">
                <div class="-success">Pague com pix para concluir</div>
                <div class="-track">{purchaseId || ''}</div>
            </div>
            <!-- watchPayment: a tela confirma o pagamento sozinha assim que o pix cair -->
            <PaymentsPixShow {chargeId} watchPayment={true} />
            <div class="spacer" />
        </div>
    {/if}
</div>

<style>
</style>
